import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    ReadingSession,
    ReadingAttempt,
    ComprehensionAttempt,
    ReadingFeedback,
    StartReadingSessionRequest,
    AudioUploadRequest,
    SubmitComprehensionRequest,
    PaginatedResponse
} from '@/services/reading/types';

// === SESSIONS ACTIONS ===
export const fetchReadingSessionsRequest = createAction<{ page?: number; pageSize?: number }>('readingSessions/fetchSessionsRequest');
export const fetchReadingSessionsSuccess = createAction<PaginatedResponse<ReadingSession>>('readingSessions/fetchSessionsSuccess');
export const fetchReadingSessionsFailure = createAction<string>('readingSessions/fetchSessionsFailure');

export const fetchReadingSessionByIdRequest = createAction<string>('readingSessions/fetchSessionByIdRequest');
export const fetchReadingSessionByIdSuccess = createAction<ReadingSession>('readingSessions/fetchSessionByIdSuccess');
export const fetchReadingSessionByIdFailure = createAction<string>('readingSessions/fetchSessionByIdFailure');

export const startReadingSessionRequest = createAction<StartReadingSessionRequest>('readingSessions/startSessionRequest');
export const startReadingSessionSuccess = createAction<ReadingSession>('readingSessions/startSessionSuccess');
export const startReadingSessionFailure = createAction<string>('readingSessions/startSessionFailure');

export const completeReadingSessionRequest = createAction<{ sessionId: string; completionData: any }>('readingSessions/completeSessionRequest');
export const completeReadingSessionSuccess = createAction<ReadingSession>('readingSessions/completeSessionSuccess');
export const completeReadingSessionFailure = createAction<string>('readingSessions/completeSessionFailure');

// === AUDIO UPLOAD ACTIONS ===
export const uploadReadingAudioRequest = createAction<AudioUploadRequest>('readingSessions/uploadAudioRequest');
export const uploadReadingAudioSuccess = createAction<{
    attempt: ReadingAttempt;
    session_progress: any;
    is_passing: boolean;
    passing_threshold: number;
    session?: ReadingSession;
}>('readingSessions/uploadAudioSuccess');
export const uploadReadingAudioFailure = createAction<string>('readingSessions/uploadAudioFailure');

export const fetchSessionAttemptsRequest = createAction<string>('readingSessions/fetchAttemptsRequest');
export const fetchSessionAttemptsSuccess = createAction<ReadingAttempt[]>('readingSessions/fetchAttemptsSuccess');
export const fetchSessionAttemptsFailure = createAction<string>('readingSessions/fetchAttemptsFailure');

// === COMPREHENSION ACTIONS ===
export const submitComprehensionRequest = createAction<SubmitComprehensionRequest>('readingSessions/submitComprehensionRequest');
export const submitComprehensionSuccess = createAction<ComprehensionAttempt[]>('readingSessions/submitComprehensionSuccess');
export const submitComprehensionFailure = createAction<string>('readingSessions/submitComprehensionFailure');

export const fetchSessionComprehensionRequest = createAction<string>('readingSessions/fetchComprehensionRequest');
export const fetchSessionComprehensionSuccess = createAction<ComprehensionAttempt[]>('readingSessions/fetchComprehensionSuccess');
export const fetchSessionComprehensionFailure = createAction<string>('readingSessions/fetchComprehensionFailure');

// === FEEDBACK ACTIONS ===
export const fetchSessionFeedbackRequest = createAction<string>('readingSessions/fetchFeedbackRequest');
export const fetchSessionFeedbackSuccess = createAction<ReadingFeedback[]>('readingSessions/fetchFeedbackSuccess');
export const fetchSessionFeedbackFailure = createAction<string>('readingSessions/fetchFeedbackFailure');

export const generateSessionFeedbackRequest = createAction<string>('readingSessions/generateFeedbackRequest');
export const generateSessionFeedbackSuccess = createAction<ReadingFeedback[]>('readingSessions/generateFeedbackSuccess');
export const generateSessionFeedbackFailure = createAction<string>('readingSessions/generateFeedbackFailure');

// === STATE INTERFACE ===
export interface ReadingSessionsState {
    // Sessions list
    sessions: ReadingSession[];
    isLoadingSessions: boolean;
    sessionsError: string | null;
    totalSessions: number;
    currentSessionPage: number;
    hasNextSessionPage: boolean;
    hasPreviousSessionPage: boolean;

    // Current session
    currentSession: ReadingSession | null;
    isLoadingSession: boolean;
    sessionError: string | null;
    isStartingSession: boolean;
    startSessionError: string | null;
    isCompletingSession: boolean;
    completeSessionError: string | null;

    // Audio attempts
    sessionAttempts: ReadingAttempt[];
    isLoadingAttempts: boolean;
    attemptsError: string | null;
    isUploadingAudio: boolean;
    uploadAudioError: string | null;

    // Comprehension
    comprehensionAttempts: ComprehensionAttempt[];
    isLoadingComprehension: boolean;
    comprehensionError: string | null;
    isSubmittingComprehension: boolean;
    submitComprehensionError: string | null;

    // Feedback
    sessionFeedback: ReadingFeedback[];
    isLoadingFeedback: boolean;
    feedbackError: string | null;
    isGeneratingFeedback: boolean;
    generateFeedbackError: string | null;
}

// === INITIAL STATE ===
const initialState: ReadingSessionsState = {
    // Sessions list
    sessions: [],
    isLoadingSessions: false,
    sessionsError: null,
    totalSessions: 0,
    currentSessionPage: 1,
    hasNextSessionPage: false,
    hasPreviousSessionPage: false,

    // Current session
    currentSession: null,
    isLoadingSession: false,
    sessionError: null,
    isStartingSession: false,
    startSessionError: null,
    isCompletingSession: false,
    completeSessionError: null,

    // Audio attempts
    sessionAttempts: [],
    isLoadingAttempts: false,
    attemptsError: null,
    isUploadingAudio: false,
    uploadAudioError: null,

    // Comprehension
    comprehensionAttempts: [],
    isLoadingComprehension: false,
    comprehensionError: null,
    isSubmittingComprehension: false,
    submitComprehensionError: null,

    // Feedback
    sessionFeedback: [],
    isLoadingFeedback: false,
    feedbackError: null,
    isGeneratingFeedback: false,
    generateFeedbackError: null,
};

// === SLICE DEFINITION ===
const readingSessionsSlice = createSlice({
    name: 'readingSessions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH SESSIONS ===
            .addCase(fetchReadingSessionsRequest, (state) => {
                state.isLoadingSessions = true;
                state.sessionsError = null;
            })
            .addCase(fetchReadingSessionsSuccess, (state, action) => {
                state.isLoadingSessions = false;
                state.sessions = action.payload.results;
                state.totalSessions = action.payload.count;
                state.hasNextSessionPage = !!action.payload.next;
                state.hasPreviousSessionPage = !!action.payload.previous;
            })
            .addCase(fetchReadingSessionsFailure, (state, action) => {
                state.isLoadingSessions = false;
                state.sessionsError = action.payload;
            })

            // === FETCH SESSION BY ID ===
            .addCase(fetchReadingSessionByIdRequest, (state) => {
                state.isLoadingSession = true;
                state.sessionError = null;
            })
            .addCase(fetchReadingSessionByIdSuccess, (state, action) => {
                state.isLoadingSession = false;
                state.currentSession = action.payload;
            })
            .addCase(fetchReadingSessionByIdFailure, (state, action) => {
                state.isLoadingSession = false;
                state.sessionError = action.payload;
            })

            // === START SESSION ===
            .addCase(startReadingSessionRequest, (state) => {
                state.isStartingSession = true;
                state.startSessionError = null;
            })
            .addCase(startReadingSessionSuccess, (state, action) => {
                state.isStartingSession = false;
                state.currentSession = action.payload;
            })
            .addCase(startReadingSessionFailure, (state, action) => {
                state.isStartingSession = false;
                state.startSessionError = action.payload;
            })

            // === COMPLETE SESSION ===
            .addCase(completeReadingSessionRequest, (state) => {
                state.isCompletingSession = true;
                state.completeSessionError = null;
            })
            .addCase(completeReadingSessionSuccess, (state, action) => {
                state.isCompletingSession = false;
                state.currentSession = action.payload;
            })
            .addCase(completeReadingSessionFailure, (state, action) => {
                state.isCompletingSession = false;
                state.completeSessionError = action.payload;
            })

            // === UPLOAD AUDIO ===
            .addCase(uploadReadingAudioRequest, (state) => {
                state.isUploadingAudio = true;
                state.uploadAudioError = null;
            })
            .addCase(uploadReadingAudioSuccess, (state, action) => {
                state.isUploadingAudio = false;

                // Add or update the attempt
                const existingAttemptIndex = state.sessionAttempts.findIndex(
                    (attempt) =>
                        attempt.sentence_number === action.payload.attempt.sentence_number &&
                        attempt.attempt_number === action.payload.attempt.attempt_number
                );

                if (existingAttemptIndex >= 0) {
                    state.sessionAttempts[existingAttemptIndex] = action.payload.attempt;
                } else {
                    state.sessionAttempts.push(action.payload.attempt);
                }

                // Update current session if provided
                if (action.payload.session) {
                    state.currentSession = action.payload.session;
                }
            })
            .addCase(uploadReadingAudioFailure, (state, action) => {
                state.isUploadingAudio = false;
                state.uploadAudioError = action.payload;
            })

            // === FETCH ATTEMPTS ===
            .addCase(fetchSessionAttemptsRequest, (state) => {
                state.isLoadingAttempts = true;
                state.attemptsError = null;
            })
            .addCase(fetchSessionAttemptsSuccess, (state, action) => {
                state.isLoadingAttempts = false;
                state.sessionAttempts = action.payload;
            })
            .addCase(fetchSessionAttemptsFailure, (state, action) => {
                state.isLoadingAttempts = false;
                state.attemptsError = action.payload;
            })

            // === SUBMIT COMPREHENSION ===
            .addCase(submitComprehensionRequest, (state) => {
                state.isSubmittingComprehension = true;
                state.submitComprehensionError = null;
            })
            .addCase(submitComprehensionSuccess, (state, action) => {
                state.isSubmittingComprehension = false;
                state.comprehensionAttempts = action.payload;
            })
            .addCase(submitComprehensionFailure, (state, action) => {
                state.isSubmittingComprehension = false;
                state.submitComprehensionError = action.payload;
            })

            // === FETCH COMPREHENSION ===
            .addCase(fetchSessionComprehensionRequest, (state) => {
                state.isLoadingComprehension = true;
                state.comprehensionError = null;
            })
            .addCase(fetchSessionComprehensionSuccess, (state, action) => {
                state.isLoadingComprehension = false;
                state.comprehensionAttempts = action.payload;
            })
            .addCase(fetchSessionComprehensionFailure, (state, action) => {
                state.isLoadingComprehension = false;
                state.comprehensionError = action.payload;
            })

            // === FETCH FEEDBACK ===
            .addCase(fetchSessionFeedbackRequest, (state) => {
                state.isLoadingFeedback = true;
                state.feedbackError = null;
            })
            .addCase(fetchSessionFeedbackSuccess, (state, action) => {
                state.isLoadingFeedback = false;
                state.sessionFeedback = action.payload;
            })
            .addCase(fetchSessionFeedbackFailure, (state, action) => {
                state.isLoadingFeedback = false;
                state.feedbackError = action.payload;
            })

            // === GENERATE FEEDBACK ===
            .addCase(generateSessionFeedbackRequest, (state) => {
                state.isGeneratingFeedback = true;
                state.generateFeedbackError = null;
            })
            .addCase(generateSessionFeedbackSuccess, (state, action) => {
                state.isGeneratingFeedback = false;
                state.sessionFeedback = action.payload;
            })
            .addCase(generateSessionFeedbackFailure, (state, action) => {
                state.isGeneratingFeedback = false;
                state.generateFeedbackError = action.payload;
            });
    },
});

export default readingSessionsSlice.reducer; 